import { captureException } from '@sentry/react';
import { setTempLeadId } from 'domains/signupDirect/misc/leadStorage';
import {
  onClearSignupLocalStorage,
  onSaveSignupToLocalStorage,
  resumeSignupFromLocalStorage,
} from 'domains/signupDirect/misc/localStorage';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { extractFiltersFromURL } from 'domains/signupDirect/misc/URLparams';
import { ResumedSignupData } from 'domains/signupDirect/ResumeLead/resumeLead';
import { ConsultationFormType } from 'domains/signupDirect/schemas/consultationSchema';
import { EvaluationFormType } from 'domains/signupDirect/schemas/evaluationSchema';
import { DirectSignupFiltersFormType, directSignupFiltersSchema } from 'domains/signupDirect/schemas/filtersSchema';
import { PreBook } from 'domains/signupDirect/schemas/preBookSchema';
import { findDefaultTimezoneOptionByState } from 'domains/timezone';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { isObject, isUndefined } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { DeepPartial } from 'react-hook-form';
import { Navigate, Outlet } from 'react-router-dom';
import { routes } from 'router/routes';
import { useTrack } from 'utils/hooks/useMixPanel';
import { useNavigation } from 'utils/hooks/useNavigation';
import { StateAbbrev } from 'utils/states';
import { EvaluationScheduleFormType } from './schemas/evaluationScheduleSchema';
import { filtersAllowSplitEvaluation } from './businessLogic/splitEvaluationCriteria';
import { useSplitEvaluationCtx } from './Evaluation/Schedule/Split/SplitEvaluationsScheduleSection';
import { PrefilledSignupAppointment } from './ResumeLead/PrefilledAppointment/types';

export type DirectSignupFilters = DirectSignupFiltersFormType;

export type ResumedLeadState = {
  shouldSkipEligibility: boolean;
  leadID: string;

  prefilledAppointment?: PrefilledSignupAppointment;
};

type SignupDirectContext = {
  filters?: DirectSignupFilters;
  incompleteFiltersForm?: DeepPartial<DirectSignupFiltersFormType>;
  incompleteEvalForm?: DeepPartial<EvaluationFormType>;
  incompleteConsultForm?: DeepPartial<ConsultationFormType>;
  resumedLeadState?: ResumedLeadState;

  evaluationSchedule?: EvaluationScheduleFormType;
  speechTherapySessionSchedule?: PreBook;

  onSubmitFilters: (newFilters: DirectSignupFilters) => void;
  onContinueToScheduleEval: () => void;
  onContinueToEvalForm: (timeSlot: EvaluationScheduleFormType) => void;
  onContinueToScheduleSts: (preBook: PreBook) => void;
  /** @deprecated not in use at the moment, replaced with onContinueToJotformRegistrationForm */
  onContinueToConsultForm: (replace?: boolean) => void;
  onContinueToJotformRegistrationForm: () => void;
  onSaveIncompleteEvalForm: (data?: DeepPartial<EvaluationFormType>) => void;
  onSaveIncompleteConsultForm: (data?: DeepPartial<ConsultationFormType>) => void;

  onResumeFromLeadData: (data: ResumedSignupData) => void;

  isResumedLead: boolean;

  onReturnToFilters: () => void;
  isLoadingSavedData: boolean;
};

export const SignupDirectContext = createContext<SignupDirectContext | null>(null);

export const useSignupDirectCtx = () => {
  const ctx = useContext(SignupDirectContext);

  if (!ctx) {
    throw new Error('Need to be used inside SignupDirectContext.Provider');
  }

  return ctx;
};

export const useSignupDirect = (): SignupDirectContext => {
  const { track } = useTrack();
  const { navigate } = useNavigation();

  const [filters, setFilters] = useState<DirectSignupFilters | undefined>();
  const [evalTimeSlot, setEvalTimeSlot] = useState<EvaluationScheduleFormType>();
  const [incompleteFiltersForm, setIncompleteFiltersForm] = useState<SignupDirectContext['incompleteFiltersForm']>();
  const [incompleteEvalForm, setIncompleteEvalForm] = useState<SignupDirectContext['incompleteEvalForm']>();
  const [incompleteConsultForm, setIncompleteConsultForm] = useState<SignupDirectContext['incompleteConsultForm']>();
  const [speechTherapySessionSchedule, setSpeechTherapySessionSchedule] = useState<PreBook>();
  const [isLoadingSavedData, setIsLoadingSavedData] = useState(true);
  const [resumedLeadState, setResumedLeadState] = useState<ResumedLeadState>();

  const { setDisplayTimezone } = useDisplayTimezone();

  const onSubmitFilters: SignupDirectContext['onSubmitFilters'] = useCallback(newFilters => {
    setFilters(newFilters);
    navigate(routes.signupDirect.landingPage);
    onUpdateDisplayTimezone(newFilters.stateCode);
  }, []);

  const onContinueToConsultForm: SignupDirectContext['onContinueToConsultForm'] = useCallback(replace => {
    navigate(routes.signupDirect.consultation.details, { replace });
  }, []);

  const onContinueToJotformRegistrationForm: SignupDirectContext['onContinueToJotformRegistrationForm'] =
    useCallback(() => {
      if (!filters) throw new Error('Expected filters to be set');

      navigate(routes.signupDirect.jotformRedirect);
    }, [filters, resumedLeadState]);

  const onContinueToScheduleEval: SignupDirectContext['onContinueToScheduleEval'] = useCallback(() => {
    if (!filters) throw new Error('Expected filters to be set');
    const filtersToCheck = filters;

    if (filtersAllowSplitEvaluation(filtersToCheck).allowed) {
      navigate(routes.signupDirect.evaluation.schedule.split.first);
    } else {
      navigate(routes.signupDirect.evaluation.schedule.single);
    }
  }, [filters]);

  const onContinueToEvalForm: SignupDirectContext['onContinueToEvalForm'] = useCallback(timeSlot => {
    setEvalTimeSlot(timeSlot);
    navigate(routes.signupDirect.evaluation.details);
  }, []);

  const onContinueToScheduleSts: SignupDirectContext['onContinueToScheduleSts'] = useCallback(
    speechTherapySessionSchedule => {
      setSpeechTherapySessionSchedule(speechTherapySessionSchedule);
      navigate(routes.signupDirect.preBook);
    },
    [],
  );

  const onReturnToFilters: SignupDirectContext['onReturnToFilters'] = useCallback(() => {
    navigate(routes.signupDirect.filters);
  }, []);

  const onSaveIncompleteEvalForm: SignupDirectContext['onSaveIncompleteEvalForm'] = useCallback(data => {
    setIncompleteEvalForm(data);
  }, []);

  const onSaveIncompleteConsultForm: SignupDirectContext['onSaveIncompleteConsultForm'] = useCallback(data => {
    setIncompleteConsultForm(data);
  }, []);

  const onResumeFromLeadData: SignupDirectContext['onResumeFromLeadData'] = useCallback(data => {
    if (data.expired) {
      onClearSignupLocalStorage();
      navigate(routes.signupDirect.filters);
      return;
    }
    setTempLeadId(data.resumedLeadState.leadID);

    setResumedLeadState(data.resumedLeadState);

    if (data.resumedLeadState.prefilledAppointment) {
      if (isUndefined(data.filters.stateCode)) {
        captureException(
          new Error(
            `Missing state code for lead ${data.resumedLeadState.leadID} when using PREFILLED APPOINTMENT, ignoring appointment`,
          ),
        );
      } else {
        const appointment = data.resumedLeadState.prefilledAppointment;
        const defaultTz = findDefaultTimezoneOptionByState(data.filters.stateCode);
        setEvalTimeSlot({
          type: 'single',
          timeZone: defaultTz?.resolvedName,
          timeSlot: {
            time: appointment.appointmentTime,
            calendarID: appointment.calendarID,
            therapistName: `${appointment.therapistFirstName} ${appointment.therapistLastName}`,
            id: 'prefilled-appointment',
            therapistID: appointment.therapistID,
          },
        });
      }
    }

    if (data.incompleteEvalForm) {
      setIncompleteEvalForm(data.incompleteEvalForm);
    }
    if (data.filters) {
      const parsedFiltersForm = directSignupFiltersSchema.safeParse({
        ...data.filters,
      } as DirectSignupFiltersFormType);

      if (parsedFiltersForm.success) {
        // Filters are complete,
        setFilters(parsedFiltersForm.data as DirectSignupFilters);
        onUpdateDisplayTimezone(parsedFiltersForm.data.stateCode as StateAbbrev);

        navigate(routes.signupDirect.landingPage);
      } else {
        setIncompleteFiltersForm(data.filters);
        navigate(routes.signupDirect.filters);
      }
    }
    if (data.incompleteEvalForm) setIncompleteEvalForm(data.incompleteEvalForm);
  }, []);

  const onSaveToLocalStorage = () => {
    onSaveSignupToLocalStorage({
      filters,
      speechTherapySessionSchedule,
      incompleteEvalForm,
      incompleteConsultForm,
      evalTimeSlot,
      resumedLeadState,
    });
  };

  useEffect(() => {
    if (!isLoadingSavedData) onSaveToLocalStorage();
  }, [
    filters,
    evalTimeSlot,
    incompleteEvalForm,
    incompleteConsultForm,
    speechTherapySessionSchedule,
    isLoadingSavedData,
    evalTimeSlot,
    resumedLeadState,
  ]);

  const onUpdateDisplayTimezone = (stateCode: StateAbbrev) => {
    const defaultTz = stateCode ? findDefaultTimezoneOptionByState(stateCode) : undefined;
    if (defaultTz) setDisplayTimezone(defaultTz);
  };

  const resumeFromLocalStorage = () => {
    const savedData = resumeSignupFromLocalStorage();

    if (savedData.filters) {
      onUpdateDisplayTimezone(savedData.filters.stateCode);
      setFilters(savedData.filters);
    }
    if (savedData.evalTimeSlot) setEvalTimeSlot(savedData.evalTimeSlot);
    if (savedData.incompleteEvalForm) setIncompleteEvalForm(savedData.incompleteEvalForm);
    if (savedData.incompleteConsultForm) setIncompleteConsultForm(savedData.incompleteConsultForm);
    if (savedData.speechTherapySessionSchedule) setSpeechTherapySessionSchedule(savedData.speechTherapySessionSchedule);
    if (savedData.resumedLeadState) setResumedLeadState(savedData.resumedLeadState);
  };

  const initialize = async () => {
    if (window.location.pathname === routes.signupDirect.resume) {
      setIsLoadingSavedData(false);
      return;
    }

    let filtersFromURL: DirectSignupFilters | undefined;
    try {
      filtersFromURL = await extractFiltersFromURL(window.location.search);
    } catch (e) {
      captureException(new Error('Failed to extract filters from URL'), {
        extra: {
          search: window.location.search,
          error: e,
        },
      });
    }

    if (filtersFromURL) {
      onUpdateDisplayTimezone(filtersFromURL.stateCode);
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.URL_FILTERS, { isResumedLead });

      onSubmitFilters(filtersFromURL);
    } else {
      resumeFromLocalStorage();
    }
    setIsLoadingSavedData(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  const isResumedLead = isObject(resumedLeadState);

  return {
    filters,
    onSubmitFilters,
    onContinueToConsultForm,
    onContinueToScheduleEval,
    onContinueToScheduleSts,
    onContinueToEvalForm,
    onReturnToFilters,
    evaluationSchedule: evalTimeSlot,
    onSaveIncompleteEvalForm,
    onSaveIncompleteConsultForm,
    incompleteConsultForm,
    incompleteEvalForm,
    incompleteFiltersForm,
    speechTherapySessionSchedule,
    isLoadingSavedData,
    onResumeFromLeadData,
    isResumedLead,
    resumedLeadState,
    onContinueToJotformRegistrationForm,
  };
};

/**
 * Short term solution for preventing opening the flow in a step its not supposed to start on
 */

export const FiltersGate = () => {
  const { speechTherapySessionSchedule } = useSignupDirectCtx();

  if (speechTherapySessionSchedule) {
    // Resume pre-booking
    return <Navigate to={routes.signupDirect.preBook} />;
  }

  return <Outlet />;
};

export const LandingPageGate = () => {
  const { filters } = useSignupDirectCtx();

  if (!filters) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  return <Outlet />;
};

export const EvalScheduleGate = () => {
  const { filters, speechTherapySessionSchedule } = useSignupDirectCtx();

  if (!filters) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  if (speechTherapySessionSchedule) {
    // Resume pre-booking
    return <Navigate to={routes.signupDirect.preBook} />;
  }

  return <Outlet />;
};

export const SecondSplitGate = () => {
  const { firstTimeSlot } = useSplitEvaluationCtx();

  if (!firstTimeSlot) {
    return <Navigate to={routes.signupDirect.evaluation.schedule.split.first} />;
  }

  return <Outlet />;
};

export const EvalFormGate = () => {
  const { evaluationSchedule, speechTherapySessionSchedule, filters } = useSignupDirectCtx();

  if (!filters) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  if (!evaluationSchedule) {
    if (filtersAllowSplitEvaluation(filters).allowed) {
      return <Navigate to={routes.signupDirect.evaluation.schedule.split.first} />;
    } else {
      return <Navigate to={routes.signupDirect.evaluation.schedule.single} />;
    }
  }

  if (speechTherapySessionSchedule) {
    // Resume pre-booking
    return <Navigate to={routes.signupDirect.preBook} />;
  }

  return <Outlet />;
};

export const ConsultFormGate = () => {
  const { filters } = useSignupDirectCtx();

  if (!filters) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  return <Outlet />;
};

export const PrebookSessionsGate = () => {
  const { speechTherapySessionSchedule } = useSignupDirectCtx();

  if (!speechTherapySessionSchedule) {
    return <Navigate to={routes.signupDirect.filters} />;
  }

  return <Outlet />;
};
